import React, { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from 'lucide-react'
import { cn } from '../../lib/cn'
import { AuthorizedMediaImage } from '../../shared/media/authorizedMedia'
import { useOverlayLayer } from './overlayStack'

export interface LightboxItem {
  src: string
  label?: string
}

export interface LightboxProps {
  isOpen: boolean
  items: LightboxItem[]
  index: number
  onIndexChange: (index: number) => void
  onClose: () => void
}

/**
 * Lightbox — полноэкранный просмотр изображений поверх модалок (z-[70]).
 * Клик по картинке / кнопка — переключение масштаба «вписать ↔ 100%»,
 * ←/→ — переключение файлов, Esc — закрыть. Фон всегда тёмный.
 */
export const Lightbox: React.FC<LightboxProps> = ({
  isOpen,
  items,
  index,
  onIndexChange,
  onClose,
}) => {
  const [zoomed, setZoomed] = useState(false)
  // Без картинок просмотрщик ничего не рисует — и в стек вставать не должен,
  // иначе невидимый слой перехватил бы Esc у окна под собой.
  const { isTopmost } = useOverlayLayer(isOpen && items.length > 0)

  useEffect(() => setZoomed(false), [index, isOpen])

  const prev = useCallback(() => {
    if (index > 0) onIndexChange(index - 1)
  }, [index, onIndexChange])
  const next = useCallback(() => {
    if (index < items.length - 1) onIndexChange(index + 1)
  }, [index, items.length, onIndexChange])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      // Клавиатурой владеет верхний оверлей: если поверх просмотрщика открыли
      // окно, оно и обрабатывает Esc, а просмотрщик молчит и событие не гасит.
      if (!isTopmost()) return
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
      if (e.key === 'ArrowLeft') prev()
      if (e.key === 'ArrowRight') next()
    }
    // capture: перехватываем раньше хоткеев модалки под лайтбоксом
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [isOpen, prev, next, onClose, isTopmost])

  if (!isOpen || !items.length) return null
  const item = items[Math.min(Math.max(index, 0), items.length - 1)]

  return (
    <div
      className="fixed inset-0 z-[70] !m-0 flex flex-col bg-black/85"
      role="dialog"
      aria-modal="true"
      aria-label={item.label || 'Просмотр изображения'}
      onClick={onClose}
    >
      <div
        className="flex shrink-0 items-center gap-2 px-4 py-3 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="min-w-0 truncate text-sm text-white/80">{item.label}</span>
        {items.length > 1 && (
          <span className="text-xs tabular-nums text-white/50">
            {index + 1} из {items.length}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          <button
            type="button"
            title={zoomed ? 'Вписать в окно' : 'Масштаб 100%'}
            onClick={() => setZoomed((z) => !z)}
            className="rounded-md p-2 text-white/70 hover:bg-white/10 hover:text-white"
          >
            {zoomed ? <ZoomOut className="h-4 w-4" /> : <ZoomIn className="h-4 w-4" />}
          </button>
          <button
            type="button"
            title="Закрыть (Esc)"
            onClick={onClose}
            className="rounded-md p-2 text-white/70 hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </span>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-14 pb-6">
        {items.length > 1 && (
          <button
            type="button"
            title="Предыдущий (←)"
            disabled={index <= 0}
            onClick={(e) => {
              e.stopPropagation()
              prev()
            }}
            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 disabled:opacity-30"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        <div
          data-testid="lightbox-backdrop"
          className={cn(
            'flex h-full w-full items-center justify-center',
            zoomed && 'overflow-auto',
          )}
          onClick={(event) => {
            event.stopPropagation()
            onClose()
          }}
        >
          <AuthorizedMediaImage
            src={item.src}
            alt={item.label || ''}
            // Единственная крупная картинка, открытая пользователем нарочно —
            // заведомо в кадре сразу, отложенная загрузка тут не нужна.
            lazy={false}
            loadingFallback={<span role="status" className="text-sm text-white/70">Загрузка изображения…</span>}
            errorFallback={<span role="alert" className="text-sm text-red-300">Изображение недоступно</span>}
            onClick={(event) => {
              event.stopPropagation()
              setZoomed((z) => !z)
            }}
            // В режиме «вписать» размер элемента равен самой картинке, а не
            // всему полю: иначе прозрачные поля по бокам остаются частью <img>,
            // и клик мимо изображения переключал бы масштаб вместо закрытия.
            className={cn(
              'rounded',
              zoomed
                ? 'max-h-none max-w-none cursor-zoom-out'
                : 'max-h-full max-w-full cursor-zoom-in object-contain',
            )}
          />
        </div>
        {items.length > 1 && (
          <button
            type="button"
            title="Следующий (→)"
            disabled={index >= items.length - 1}
            onClick={(e) => {
              e.stopPropagation()
              next()
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 disabled:opacity-30"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  )
}

export default Lightbox
